import { groupBy } from '../../../../utils/group-by';
import { plural } from '../../../../utils/plural';
import { formatAmount, formatNumber, normalizeAmount } from '../../../../utils/format-amount';
import { sortBy } from '../../../../utils/sort-by';
import { BomStructure } from '../../../../models/bom-structure';
import { OperationType } from './operation-type.model';
import { WorkLine } from './work-line.model';

export type Urgency = 'overdue' | 'soon' | 'normal';

export interface DeadlineVm {
  label: string;
  urgency: Urgency;
}

// One cell of the sheet: this row at this operation. Rows carry one cell per dictionary
// entry, in dictionary order — a blank cell is just zero; "does not go through this" is
// the explicit isSkipped (0048), rendered as a dash.
export interface OperationCellVm {
  operationTypeId: number;
  doneAmount: number;
  remainingAmount: number;   // the column's remaining — what a report there can take
  workableNow: number;       // remaining, capped by what the children allow — the prefill
  isDone: boolean;           // the column is finished for this row
  isSkipped: boolean;        // a group cell only when every line of the group skips the operation
  lastReportedLabel: string | null; // `ostatnio 12.09`
}

export interface WorkLineVm extends WorkLine {
  remainingAmount: number;
  isDone: boolean;
  deadlineLabel: string;
  isOverdue: boolean;
  workableNow: number;
  isBlocked: boolean;
  blockedByLabel: string | null;
  workableLabel: string | null;
  operations: OperationCellVm[];
}

// Per-line readiness: how much of a line can physically be worked on now,
// given what its child lines have already reported.
export interface LineReadiness {
  workableNow: number;
  buildable: number; // units the children allow in total, done or not
  blockedBy: WorkLine[];
}

export interface ItemGroupVm {
  bomItemId: number;
  itemName: string;
  unit: string;
  structure: BomStructure;   // the first line's, like the photo (0108)
  hasDocumentation: boolean;
  primaryPhotoAttachmentId: number | null; // the first line's — lines ordered with different photos show one
  metaLabel: string;
  requiredTotal: number;
  doneTotal: number;
  remainingTotal: number;
  workableNowTotal: number;
  isDone: boolean;
  isBlocked: boolean;
  blockedByLabel: string | null;
  workableLabel: string | null;
  // Nearest deadline among lines that still have remaining quantity — not nearest overall,
  // otherwise every long-lived part glows red forever.
  deadline: DeadlineVm | null;
  operations: OperationCellVm[];
  lines: WorkLineVm[];
}

// Where a row stands at the selected stage (Wg etapów). Each piece waits at exactly one
// stage: the next non-skipped one after the furthest stage it was reported at.
export type StageState = 'waiting' | 'notArrived' | 'skipped' | 'passed';

export interface StageLineVm extends WorkLineVm {
  waiting: number;
  doneAtStage: number;
  remainingAtStage: number;  // what a report at the stage can take; zero on a skipped or finished line
  state: StageState;
  isLearnedSkip: boolean;    // skipped from the part's memory, not by a decision on this line (0047)
  fromStageName: string | null; // the stage the waiting pieces come from; null at the line's first stage
  metaLabel: string | null;
  canSkip: boolean;
}

// One row of Wg etapów: an item under its parent, summed across orders. The same item
// under two parents is two rows — its route can depend on the parent.
export interface StageGroupVm {
  key: string;
  operationTypeId: number;   // the stage these numbers are for
  bomItemId: number;
  itemName: string;
  parentName: string | null; // null = top-level product
  unit: string;
  structure: BomStructure;   // the first line's, like the photo (0108)
  depth: number;             // the anchor line's — the row sits at its tree position, so it indents by its depth (0108)
  hasDocumentation: boolean;
  primaryPhotoAttachmentId: number | null; // the first line's — lines ordered with different photos show one
  metaLabel: string;
  waitingTotal: number;
  doneAtStageTotal: number;
  requiredTotal: number;
  remainingAtStageTotal: number;
  state: StageState;
  canSkip: boolean;
  isDone: boolean;
  deadline: DeadlineVm | null;
  lines: StageLineVm[];
}

// The row the DRZEWO card follows under Wg etapów (0098): a group (lineId null — the line that
// gives the group its deadline stands for it) or one order line inside it
export interface StageRowFocus {
  groupKey: string;
  lineId: number | null;
}

// The focused row's zlecenie tree: the root position that contains the focused line, down to
// every leaf — the siblings and the cousins are the point, so the whole position, not the branch
export interface ContextTreeVm {
  orderNumber: string;
  orderName: string;
  orderCount: number; // distinct zlecenia in the focused group — `1 z 3` in the header
  root: ContextNodeVm;
}

export interface ContextNodeVm {
  lineId: number;
  name: string;
  amount: number;
  unit: string;
  isFocused: boolean;
  marker: string | null; // where the line stands at the selected stage; null while nothing has arrived
  // what the node's menu offers at the selected stage (0102) — the line row's button rules, read off
  // the same StageLineVm as the marker; the node component ignores them
  canReport: boolean;
  canSkip: boolean;
  canRestore: boolean;
  isDone: boolean;        // tells `gotowe` from `po tym etapie` when there is nothing to act on
  waiting: number;        // the report's prefill
  remainingAtStage: number; // what it can take
  children: ContextNodeVm[];
}

export interface OrderGroupVm {
  orderId: number;
  orderNumber: string;
  orderName: string;
  deadline: DeadlineVm;
  lineCount: number;
  doneLineCount: number;
  progressPercent: number;
  isDone: boolean;
  lines: WorkLineVm[];
}

export interface OverseerSummary {
  ordersInProgress: number;
  ordersOverdue: number;
  ordersDueThisWeek: number;
}

export namespace OverseerViewModel {
  // Every child gates, material leaves included — SPAW-MET reports operations
  // (cięcie, gięcie) directly on raw-material lines, so they are real work. Except a child
  // that skips every stage (0115): it waits nowhere, so nothing waits on it either — a
  // bought bolt, or a line skipped by hand everywhere. With no stages every child gates.
  export function computeReadiness(lines: WorkLine[], operationTypes: OperationType[]): Map<number, LineReadiness> {
    const childrenByParent = new Map<number, WorkLine[]>();

    for (const line of lines) {
      if (line.parentLineId != null && !skipsEveryStage(line, operationTypes)) {
        const siblings = childrenByParent.get(line.parentLineId) ?? [];
        siblings.push(line);
        childrenByParent.set(line.parentLineId, siblings);
      }
    }

    return new Map(lines.map(line => [
      line.lineId,
      lineReadiness(line, childrenByParent.get(line.lineId) ?? [])
    ]));
  }

  // A welded frame contains its bolts (0117): a line that skips every stage completes with its
  // parent, in proportion — max(own, min(required, parent's effective done × required /
  // parent's required)). The parent's effective done, so a chain of such lines carries through;
  // not floored, amounts are order totals; max, never a sum, a hand report and the carry are the
  // same pieces. A root has no parent to complete with. StageSkips.EffectiveDone on the API is the
  // same rule — the planner and the zlecenie page read that one.
  export function computeEffectiveDone(lines: WorkLine[], operationTypes: OperationType[]): Map<number, number> {
    const linesById = new Map(lines.map(line => [line.lineId, line]));
    const effective = new Map<number, number>();

    const effectiveDone = (line: WorkLine): number => {
      const known = effective.get(line.lineId);

      if (known !== undefined) {
        return known;
      }

      const parent = line.parentLineId != null ? linesById.get(line.parentLineId) : undefined;
      let done = line.doneAmount;

      if (parent && skipsEveryStage(line, operationTypes)) {
        const carried = parent.requiredAmount > 0
          ? Math.min(line.requiredAmount, normalizeAmount(effectiveDone(parent) * line.requiredAmount / parent.requiredAmount))
          : 0;

        done = Math.max(done, carried);
      }

      effective.set(line.lineId, done);

      return done;
    };

    lines.forEach(effectiveDone);

    return effective;
  }

  // The lines as the views count them: done is the effective done. WorkLine.doneAmount in the
  // store stays the line's own reports — reports are allocated and undone against it
  export function withEffectiveDone(lines: WorkLine[], operationTypes: OperationType[]): WorkLine[] {
    const effective = computeEffectiveDone(lines, operationTypes);
    return lines.map(line => ({ ...line, doneAmount: effective.get(line.lineId)! }));
  }

  // Nothing to do on such a line — it completes with its parent (0117), so Do zrobienia never lists it
  export function completesWithParent(line: WorkLine, operationTypes: OperationType[]): boolean {
    return line.parentLineId != null && skipsEveryStage(line, operationTypes);
  }

  export function mapItemGroups(
    lines: WorkLine[],
    readiness: Map<number, LineReadiness>,
    operationTypes: OperationType[]
  ): ItemGroupVm[] {
    const byItem = groupBy(lines, line => line.bomItemId);

    const groups = Object.values(byItem).map(itemLines => {
      const first = itemLines[0];
      const openLines = itemLines.filter(line => WorkLine.remaining(line) > 0);
      const nearestOpenDeadline = openLines.length > 0
        ? (sortBy(openLines, line => line.deadline ?? NO_DEADLINE_SORT_KEY)[0].deadline ?? null)
        : null;

      const orderCount = new Set(itemLines.map(line => line.orderId)).size;

      // Sort: dated open items by urgency, then open items without a deadline, done items last
      const sortKey = openLines.length === 0
        ? `${DONE_SORT_KEY}|${first.itemName}`
        : `${nearestOpenDeadline ?? NO_DEADLINE_SORT_KEY}|${first.itemName}`;

      const requiredTotal = sum(itemLines, line => line.requiredAmount);
      const remainingTotal = sum(itemLines, line => WorkLine.remaining(line));
      const workableNowTotal = sum(itemLines, line => readiness.get(line.lineId)!.workableNow);
      const isBlocked = openLines.length > 0 && workableNowTotal < EPSILON;
      const blockingNames = unique(
        itemLines.flatMap(line => readiness.get(line.lineId)!.blockedBy.map(blocker => blocker.itemName))
      );
      const lineVms = itemLines.map(line => toWorkLineVm(line, readiness, operationTypes));

      return {
        bomItemId: first.bomItemId,
        itemName: first.itemName,
        // one item, one unit — the lines agree unless the catalog's unit was edited between two snapshots
        unit: first.unit,
        structure: first.structure,
        hasDocumentation: first.hasDocumentation,
        primaryPhotoAttachmentId: first.primaryPhotoAttachmentId,
        metaLabel: metaLabel(itemLines, orderCount),
        requiredTotal,
        doneTotal: sum(itemLines, line => line.doneAmount),
        remainingTotal,
        workableNowTotal,
        isDone: openLines.length === 0,
        isBlocked,
        blockedByLabel: isBlocked && blockingNames.length > 0 ? `czeka na: ${blockingNames.join(', ')}` : null,
        workableLabel: workableLabel(workableNowTotal, remainingTotal, first.unit),
        deadline: openLines.length > 0 ? toDeadlineVm(nearestOpenDeadline) : null,
        operations: groupOperationCells(lineVms, requiredTotal, operationTypes),
        lines: sortBy(
          lineVms,
          line => `${line.deadline ?? NO_DEADLINE_SORT_KEY}|${String(line.treePosition).padStart(6, '0')}`
        ),
        sortKey
      };
    });

    // Most urgent first, fully covered items sink to the bottom.
    return sortBy(groups, group => group.sortKey);
  }

  export function mapOrderGroups(
    lines: WorkLine[],
    readiness: Map<number, LineReadiness>,
    operationTypes: OperationType[]
  ): OrderGroupVm[] {
    const byOrder = groupBy(lines, line => line.orderId);

    const groups = Object.values(byOrder).map(orderLines => {
      const first = orderLines[0];
      const doneLineCount = orderLines.filter(line => WorkLine.remaining(line) === 0).length;
      // Counted in parts like the planner bar (0112): each line weighs one whatever its unit,
      // a partly reported line by its share — kg and m never add up into a total
      const progressShare = sum(orderLines, line => WorkLine.remaining(line) === 0 ? 1 : line.doneAmount / line.requiredAmount);

      return {
        orderId: first.orderId,
        orderNumber: first.orderNumber,
        orderName: first.orderName,
        deadline: toDeadlineVm(first.deadline),
        lineCount: orderLines.length,
        doneLineCount,
        progressPercent: Math.round(100 * progressShare / orderLines.length),
        isDone: doneLineCount === orderLines.length,
        lines: sortBy(orderLines.map(line => toWorkLineVm(line, readiness, operationTypes)), line => line.treePosition),
        rawDeadline: first.deadline ?? NO_DEADLINE_SORT_KEY
      };
    });

    return sortBy(groups, group => group.rawDeadline);
  }

  // null, or an entry that is gone from the dictionary, means the first one
  export function selectedOperationType(operationTypes: OperationType[], operationTypeId: number | null): OperationType | null {
    return operationTypes.find(type => type.id === operationTypeId) ?? operationTypes[0] ?? null;
  }

  // The item under its parent: own BOM item plus the parent line's. Top-level lines share "no parent".
  export function stageGroupKey(line: WorkLine, linesById: Map<number, WorkLine>): string {
    const parent = line.parentLineId != null ? linesById.get(line.parentLineId) : undefined;
    return `${line.bomItemId}|${parent?.bomItemId ?? ''}`;
  }

  export function mapStageGroups(
    lines: WorkLine[],
    readiness: Map<number, LineReadiness>,
    operationTypes: OperationType[],
    operationTypeId: number
  ): StageGroupVm[] {
    const stageIndex = operationTypes.findIndex(type => type.id === operationTypeId);

    if (stageIndex < 0) {
      return [];
    }

    const linesById = new Map(lines.map(line => [line.lineId, line]));
    const byKey = groupBy(lines, line => stageGroupKey(line, linesById));

    const groups = Object.entries(byKey).map(([key, groupLines]) => {
      const first = groupLines[0];
      const openLines = groupLines.filter(line => WorkLine.remaining(line) > 0);
      const nearestOpenDeadline = openLines.length > 0
        ? (sortBy(openLines, line => line.deadline ?? NO_DEADLINE_SORT_KEY)[0].deadline ?? null)
        : null;

      const lineVms = groupLines.map(line => toStageLineVm(line, readiness, operationTypes, stageIndex));
      const waitingTotal = sum(lineVms, line => line.waiting);
      const doneAtStageTotal = sum(lineVms, line => line.doneAtStage);
      const state = groupStageState(lineVms, waitingTotal);

      // Tree order (0104): the zlecenia by deadline (undated last, same day in creation order),
      // each flattened as DRZEWO draws it. A row sits where its anchor line sits — the line the
      // card shows for it — and tree positions never change, so no report moves a row; a done
      // row stays put, struck through.
      const anchor = anchorLine(groupLines);
      const sortKey = `${anchor.deadline ?? NO_DEADLINE_SORT_KEY}|${String(anchor.orderId).padStart(10, '0')}|${String(anchor.treePosition).padStart(6, '0')}`;

      return {
        key,
        operationTypeId,
        bomItemId: first.bomItemId,
        itemName: first.itemName,
        parentName: first.parentName,
        unit: first.unit,
        structure: first.structure,
        depth: anchor.depth,
        hasDocumentation: first.hasDocumentation,
        primaryPhotoAttachmentId: first.primaryPhotoAttachmentId,
        metaLabel: stageGroupMetaLabel(groupLines, lineVms, readiness, state, doneAtStageTotal),
        waitingTotal,
        doneAtStageTotal,
        requiredTotal: sum(groupLines, line => line.requiredAmount),
        remainingAtStageTotal: sum(lineVms, line => line.remainingAtStage),
        state,
        // Nic do zrobienia is offered only while nothing is reported at the stage
        canSkip: state === 'waiting' && doneAtStageTotal < EPSILON,
        isDone: openLines.length === 0,
        deadline: openLines.length > 0 ? toDeadlineVm(nearestOpenDeadline) : null,
        lines: sortBy(
          lineVms,
          line => `${line.deadline ?? NO_DEADLINE_SORT_KEY}|${String(line.treePosition).padStart(6, '0')}`
        ),
        sortKey
      };
    });

    return sortBy(groups, group => group.sortKey);
  }

  // Per stage: how many rows of Wg etapów have something waiting there — the strip's counts
  export function mapStageCounts(
    lines: WorkLine[],
    readiness: Map<number, LineReadiness>,
    operationTypes: OperationType[]
  ): Record<number, number> {
    const linesById = new Map(lines.map(line => [line.lineId, line]));
    const byKey = Object.values(groupBy(lines, line => stageGroupKey(line, linesById)))
      .map(groupLines => groupLines.map(line => lineStages(line, readiness.get(line.lineId)!, operationTypes)));

    return Object.fromEntries(operationTypes.map((type, index) => [
      type.id,
      byKey.filter(groupStages => groupStages.some(stages => stages[index].waiting > EPSILON)).length
    ]));
  }

  export function mapSummary(lines: WorkLine[]): OverseerSummary {
    const byOrder = Object.values(groupBy(lines, line => line.orderId));
    const openOrders = byOrder.filter(orderLines => orderLines.some(line => WorkLine.remaining(line) > 0));
    const datedOpenOrders = openOrders.filter(orderLines => orderLines[0].deadline != null);

    return {
      ordersInProgress: byOrder.length,
      ordersOverdue: datedOpenOrders.filter(orderLines => daysUntil(orderLines[0].deadline!) < 0).length,
      ordersDueThisWeek: datedOpenOrders.filter(orderLines => {
        const days = daysUntil(orderLines[0].deadline!);
        return days >= 0 && days <= 7;
      }).length
    };
  }

  // The DRZEWO card (0098): the zlecenie tree around the focused row, every node marked with
  // its state at the selected stage. Derived from the lines, not the rows, so the focus
  // survives a stage change, the filter, and the row leaving the list after a report.
  export function mapContextTree(
    lines: WorkLine[],
    readiness: Map<number, LineReadiness>,
    operationTypes: OperationType[],
    operationTypeId: number | null,
    focus: StageRowFocus | null
  ): ContextTreeVm | null {
    if (!focus) {
      return null;
    }

    const linesById = new Map(lines.map(line => [line.lineId, line]));
    const groupLines = lines.filter(line => stageGroupKey(line, linesById) === focus.groupKey);

    // A group row stands for its anchor line — the one the row is sorted by in mapStageGroups
    const focused = focus.lineId != null
      ? groupLines.find(line => line.lineId === focus.lineId)
      : anchorLine(groupLines);

    if (!focused) {
      return null;
    }

    let root = focused;

    while (root.parentLineId != null && linesById.has(root.parentLineId)) {
      root = linesById.get(root.parentLineId)!;
    }

    const stageIndex = operationTypes.findIndex(type => type.id === operationTypeId);
    const childrenByParent = groupBy(
      lines.filter(line => line.orderId === focused.orderId && line.parentLineId != null),
      line => line.parentLineId!
    );

    const toNode = (line: WorkLine): ContextNodeVm => {
      const atStage = stageIndex >= 0 ? toStageLineVm(line, readiness, operationTypes, stageIndex) : null;

      return {
        lineId: line.lineId,
        name: line.itemName,
        amount: line.requiredAmount,
        unit: line.unit,
        isFocused: line.lineId === focused.lineId,
        marker: atStage ? stageMarker(atStage) : null,
        canReport: atStage != null && !atStage.isDone && atStage.remainingAtStage > 0,
        canSkip: atStage?.canSkip ?? false,
        canRestore: atStage?.state === 'skipped',
        isDone: WorkLine.remaining(line) === 0,
        waiting: atStage?.waiting ?? 0,
        remainingAtStage: atStage?.remainingAtStage ?? 0,
        children: sortBy(childrenByParent[line.lineId] ?? [], child => child.treePosition).map(toNode)
      };
    };

    return {
      orderNumber: focused.orderNumber,
      orderName: focused.orderName,
      orderCount: new Set(groupLines.map(line => line.orderId)).size,
      root: toNode(root)
    };
  }
}

const NO_DEADLINE_SORT_KEY = '9998-12-31';
const DONE_SORT_KEY = '9999-12-31';
const EPSILON = 1e-9;

// The line a Wg etapów row stands for: a row sums one part under one parent across zlecenia,
// so the first of its lines — by deadline (undated last), then tree position — lends it a place
// in the list and a tree in the DRZEWO card. One rule for both, so the list and the card agree
// by construction. Over all the group's lines, not the open ones: the row stays still while
// its earliest zlecenie finishes (0104).
function anchorLine(groupLines: WorkLine[]): WorkLine {
  return sortBy(
    groupLines,
    line => `${line.deadline ?? NO_DEADLINE_SORT_KEY}|${String(line.treePosition).padStart(6, '0')}`
  )[0];
}

function skipsEveryStage(line: WorkLine, operationTypes: OperationType[]): boolean {
  return operationTypes.length > 0 && operationTypes.every(type => WorkLine.skips(line, type.id));
}

// Proportional readiness: amounts are order totals, so a child's per-unit ratio is
// child.required / parent.required. Buildable units are floored — safe, because only
// assemblies (counted in pieces) have children; continuous-unit materials are leaves.
function lineReadiness(line: WorkLine, children: WorkLine[]): LineReadiness {
  const remaining = WorkLine.remaining(line);

  if (children.length === 0) {
    return { workableNow: remaining, buildable: line.requiredAmount, blockedBy: [] };
  }

  const perChild = children
    .filter(child => child.requiredAmount > EPSILON)
    .map(child => ({
      child,
      buildable: Math.floor(child.doneAmount * line.requiredAmount / child.requiredAmount + EPSILON)
    }));

  const buildable = Math.min(line.requiredAmount, ...perChild.map(x => x.buildable));
  const workableNow = Math.max(0, normalizeAmount(Math.min(buildable - line.doneAmount, remaining)));

  const blockedBy = remaining > EPSILON && workableNow < EPSILON
    ? perChild
      .filter(x => x.buildable <= buildable + EPSILON && WorkLine.remaining(x.child) > EPSILON)
      .map(x => x.child)
    : [];

  return { workableNow, buildable, blockedBy };
}

function workableLabel(workableNow: number, remaining: number, unit: string): string | null {
  const isPartial = workableNow > EPSILON && workableNow + EPSILON < remaining;
  return isPartial ? `możliwe teraz: ${formatAmount(workableNow, unit)}` : null;
}

function toWorkLineVm(line: WorkLine, readiness: Map<number, LineReadiness>, operationTypes: OperationType[]): WorkLineVm {
  const remaining = WorkLine.remaining(line);
  const { workableNow, buildable, blockedBy } = readiness.get(line.lineId)!;
  const isBlocked = remaining > 0 && workableNow < EPSILON;

  return {
    ...line,
    remainingAmount: remaining,
    isDone: remaining === 0,
    deadlineLabel: line.deadline ? formatDate(line.deadline) : '—',
    isOverdue: line.deadline != null && daysUntil(line.deadline) < 0,
    workableNow,
    isBlocked,
    blockedByLabel: isBlocked && blockedBy.length > 0
      ? `czeka na: ${blockedBy.map(b => `${b.itemName} (jeszcze ${formatAmount(WorkLine.remaining(b), b.unit)})`).join(', ')}`
      : null,
    workableLabel: workableLabel(workableNow, remaining, line.unit),
    operations: operationTypes.map(type => lineOperationCell(line, type.id, buildable))
  };
}

// The readiness cap is a prefill, not a limit: the column's remaining stays the max,
// a report is a statement of fact (0025).
function lineOperationCell(line: WorkLine, operationTypeId: number, buildable: number): OperationCellVm {
  const doneAmount = WorkLine.doneAt(line, operationTypeId);
  const lastReportedAt = line.operationProgress.find(p => p.operationTypeId === operationTypeId)?.lastReportedAt ?? null;

  return {
    operationTypeId,
    doneAmount,
    remainingAmount: WorkLine.remainingAt(line, operationTypeId),
    workableNow: Math.max(0, normalizeAmount(Math.min(line.requiredAmount, buildable) - doneAmount)),
    isDone: line.requiredAmount > EPSILON && doneAmount + EPSILON >= line.requiredAmount,
    isSkipped: WorkLine.skips(line, operationTypeId),
    lastReportedLabel: lastReportedAt ? lastReportedLabel(lastReportedAt) : null
  };
}

// A group's cell sums its lines' cells; the date is the latest across them.
function groupOperationCells(lineVms: WorkLineVm[], requiredTotal: number, operationTypes: OperationType[]): OperationCellVm[] {
  return operationTypes.map((type, index) => {
    const cells = lineVms.map(line => line.operations[index]);
    const doneAmount = sum(cells, cell => cell.doneAmount);
    const lastReportedAt = lineVms
      .flatMap(line => line.operationProgress)
      .filter(p => p.operationTypeId === type.id)
      .map(p => p.lastReportedAt)
      .sort()
      .at(-1) ?? null;

    return {
      operationTypeId: type.id,
      doneAmount,
      remainingAmount: sum(cells, cell => cell.remainingAmount),
      workableNow: sum(cells, cell => cell.workableNow),
      isDone: requiredTotal > EPSILON && doneAmount + EPSILON >= requiredTotal,
      isSkipped: cells.every(cell => cell.isSkipped),
      lastReportedLabel: lastReportedAt ? lastReportedLabel(lastReportedAt) : null
    };
  });
}

// One line at one stage of the dictionary, in plant order (0046, The rule).
interface LineStage {
  isSkipped: boolean;
  doneAmount: number;
  reached: number;           // pieces that are at this stage's output or further
  waiting: number;           // arrived from the previous non-skipped stage, not yet reached
  from: OperationType | null; // that previous stage; null = this is the line's first one
}

// Only the next column: reached = the largest amount reported here or anywhere later (or
// the line's done amount) — a downstream report moves pieces past the stages in between.
// What arrives is what the previous non-skipped stage reached; at the first one, what the
// children allow (0025). Skipped stages are stepped over and wait for nothing.
function lineStages(line: WorkLine, readiness: LineReadiness, operationTypes: OperationType[]): LineStage[] {
  const reached: number[] = [];
  let furthest = line.doneAmount;

  for (let index = operationTypes.length - 1; index >= 0; index--) {
    furthest = Math.max(furthest, WorkLine.doneAt(line, operationTypes[index].id));
    reached[index] = furthest;
  }

  let arrived = Math.min(line.requiredAmount, readiness.buildable);
  let from: OperationType | null = null;

  return operationTypes.map((type, index) => {
    if (WorkLine.skips(line, type.id)) {
      return { isSkipped: true, doneAmount: 0, reached: reached[index], waiting: 0, from: null };
    }

    const stage = {
      isSkipped: false,
      doneAmount: WorkLine.doneAt(line, type.id),
      reached: reached[index],
      waiting: Math.max(0, normalizeAmount(arrived - reached[index])),
      from
    };

    arrived = reached[index];
    from = type;

    return stage;
  });
}

function toStageLineVm(
  line: WorkLine,
  readiness: Map<number, LineReadiness>,
  operationTypes: OperationType[],
  stageIndex: number
): StageLineVm {
  const lineVm = toWorkLineVm(line, readiness, operationTypes);
  const stage = lineStages(line, readiness.get(line.lineId)!, operationTypes)[stageIndex];
  const state = lineStageState(line, stage);
  const fromStageName = state === 'waiting' ? (stage.from?.name ?? null) : null;
  const isLearnedSkip = WorkLine.skipIsLearned(line, operationTypes[stageIndex].id);

  const metaParts = [
    stage.doneAmount > EPSILON && state !== 'passed' ? 'w toku' : null,
    fromStageName ? `po: ${fromStageName}` : null,
    state === 'notArrived' ? (lineVm.blockedByLabel ?? 'jeszcze nie dotarło') : null,
    state === 'skipped' ? skippedLabel([{ isLearnedSkip, structure: line.structure }]) : null
  ];

  return {
    ...lineVm,
    waiting: stage.waiting,
    doneAtStage: stage.doneAmount,
    // A finished line may still have an empty column (finished with the plain Zgłoś) —
    // its pieces are long past the stage, so a report from here has no room on it
    remainingAtStage: stage.isSkipped || lineVm.isDone ? 0 : WorkLine.remainingAt(line, operationTypes[stageIndex].id),
    state,
    isLearnedSkip,
    fromStageName,
    metaLabel: metaParts.filter(part => part != null).join(' · ') || null,
    canSkip: state === 'waiting' && stage.doneAmount < EPSILON
  };
}

// One word per tree node — the same numbers the row shows, read for the decision: `czeka 2`
// while anything waits, `pomija` on a skipped line (learned or decided alike), `w toku` when
// something is reported here and nothing more waits, `✓` past the stage or finished
function stageMarker(line: StageLineVm): string | null {
  switch (line.state) {
    case 'skipped':
      return 'pomija';
    case 'waiting':
      return `czeka ${formatNumber(line.waiting)}`;
    case 'passed':
      return '✓';
    case 'notArrived':
      return line.doneAtStage > EPSILON ? 'w toku' : null;
  }
}

function lineStageState(line: WorkLine, stage: LineStage): StageState {
  if (stage.isSkipped) {
    return 'skipped';
  }
  if (stage.waiting > EPSILON) {
    return 'waiting';
  }
  return stage.reached + EPSILON >= line.requiredAmount ? 'passed' : 'notArrived';
}

// A row is work to do while anything waits; otherwise the least finished of its lines speaks
function groupStageState(lines: StageLineVm[], waitingTotal: number): StageState {
  if (waitingTotal > EPSILON) {
    return 'waiting';
  }
  if (lines.some(line => line.state === 'notArrived')) {
    return 'notArrived';
  }
  return lines.some(line => line.state === 'skipped') ? 'skipped' : 'passed';
}

function stageGroupMetaLabel(
  groupLines: WorkLine[],
  lineVms: StageLineVm[],
  readiness: Map<number, LineReadiness>,
  state: StageState,
  doneAtStageTotal: number
): string {
  const first = groupLines[0];
  const orderCount = new Set(groupLines.map(line => line.orderId)).size;
  const orders = `${orderCount} ${plural(orderCount, 'zlecenie', 'zlecenia', 'zleceń')}`;

  // The stages the waiting pieces come from — several when the lines differ
  const fromNames = unique(lineVms.flatMap(line => line.fromStageName ?? []));

  const blockingNames = unique(lineVms
    .filter(line => line.state === 'notArrived')
    .flatMap(line => readiness.get(line.lineId)!.blockedBy.map(blocker => blocker.itemName)));

  const parts = [
    first.parentName ? `do: ${first.parentName}` : 'produkt końcowy',
    groupLines.length === 1
      ? null
      : groupLines.length === orderCount
        ? orders
        : `${orders} · ${groupLines.length} ${plural(groupLines.length, 'linia', 'linie', 'linii')}`,
    doneAtStageTotal > EPSILON && state !== 'passed' ? 'w toku' : null,
    fromNames.length > 0 ? `po: ${fromNames.join(', ')}` : null,
    state === 'notArrived'
      ? (blockingNames.length > 0 ? `czeka na: ${blockingNames.join(', ')}` : 'jeszcze nie dotarło')
      : null,
    // The row says why only when nobody decided on any of its lines
    state === 'skipped'
      ? skippedLabel(lineVms.filter(line => line.state === 'skipped'))
      : null
  ];

  return parts.filter(part => part != null).join(' · ');
}

// A skip nobody decided on is learned: from the part's memory, or — on a bought part — the
// purchased default (0115). The list does not tell the two apart; on a bought part both
// read as the ETAPY card's default does
function skippedLabel(skippedLines: { isLearnedSkip: boolean; structure: BomStructure }[]): string {
  if (!skippedLines.every(line => line.isLearnedSkip)) {
    return 'pomijany';
  }

  return skippedLines.every(line => line.structure === 'Purchased') ? 'pomijany · część kupiona' : 'pomijany · jak ostatnio';
}

function lastReportedLabel(isoTimestamp: string): string {
  return `ostatnio ${formatDate(isoTimestamp)}`;
}

function unique(values: string[]): string[] {
  return [...new Set(values)];
}

function toDeadlineVm(deadline: string | null): DeadlineVm {
  if (!deadline) {
    return { label: 'brak terminu', urgency: 'normal' };
  }

  const days = daysUntil(deadline);
  const date = formatDate(deadline);

  if (days < 0) {
    return { label: `po terminie · ${date}`, urgency: 'overdue' };
  }
  if (days === 0) {
    return { label: `dziś · ${date}`, urgency: 'soon' };
  }
  if (days === 1) {
    return { label: `jutro · ${date}`, urgency: 'soon' };
  }
  if (days <= 3) {
    return { label: `za ${days} dni · ${date}`, urgency: 'soon' };
  }
  return { label: `za ${days} dni · ${date}`, urgency: 'normal' };
}

function metaLabel(itemLines: WorkLine[], orderCount: number): string {
  if (itemLines.length === 1) {
    const line = itemLines[0];
    return line.parentName ? `do: ${line.parentName}` : 'produkt końcowy';
  }

  const orders = `${orderCount} ${plural(orderCount, 'zlecenie', 'zlecenia', 'zleceń')}`;

  if (itemLines.length === orderCount) {
    return orders;
  }

  return `${orders} · ${itemLines.length} ${plural(itemLines.length, 'linia', 'linie', 'linii')}`;
}

function daysUntil(isoDate: string): number {
  const target = startOfDay(new Date(isoDate));
  const today = startOfDay(new Date());
  return Math.round((target.getTime() - today.getTime()) / 86_400_000);
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function formatDate(isoDate: string): string {
  const date = new Date(isoDate);
  return `${date.getDate()}.${String(date.getMonth() + 1).padStart(2, '0')}`;
}

// Normalized (0111): three 18.6 m lines total 55.8, not 55.800000000000004
function sum<T>(items: T[], selector: (item: T) => number): number {
  return normalizeAmount(items.reduce((acc, item) => acc + selector(item), 0));
}
